const STUDIO_SLIDER_MQ = "(min-width: 1199px)";
const REDUCED_MOTION_MQ = "(prefers-reduced-motion: reduce)";
const CUSTOMIZE_SLICK_BREAKPOINT = 1199;

function initStudioSliderRoot(root) {
  const mq = window.matchMedia(STUDIO_SLIDER_MQ);
  const motionMq = window.matchMedia(REDUCED_MOTION_MQ);
  const items = [...root.querySelectorAll("[data-studio-slider-item]")];
  const triggers = items.map((item) =>
    item.querySelector("[data-studio-slider-trigger]"),
  );
  const visuals = [...root.querySelectorAll("[data-studio-slider-visual]")];
  const progressBars = items.map((item) =>
    item.querySelector("[data-studio-slider-progress]"),
  );

  if (
    items.length === 0 ||
    triggers.some((t) => !t) ||
    progressBars.some((b) => !b)
  ) {
    return;
  }

  const intervalMs = Math.max(
    3000,
    parseInt(root.getAttribute("data-studio-slider-interval") || "7000", 10) ||
      7000,
  );

  let autoplayTimerId = null;

  function getActiveIndex() {
    const i = items.findIndex((item) => item.classList.contains("is-active"));
    return i >= 0 ? i : 0;
  }

  function resetProgressBars() {
    progressBars.forEach((bar) => {
      bar.style.transition = "none";
      bar.style.width = "0%";
    });
  }

  function stopAutoplay() {
    if (autoplayTimerId !== null) {
      clearTimeout(autoplayTimerId);
      autoplayTimerId = null;
    }
    resetProgressBars();
  }

  function applyReducedMotionProgress() {
    resetProgressBars();
    if (!mq.matches || !motionMq.matches) return;
    const i = getActiveIndex();
    progressBars[i].style.width = "100%";
  }

  function startAutoplayCycle() {
    stopAutoplay();
    if (!mq.matches) return;
    if (motionMq.matches) {
      applyReducedMotionProgress();
      return;
    }

    const index = getActiveIndex();
    const bar = progressBars[index];

    void bar.offsetWidth;
    bar.style.transition = `width ${intervalMs}ms linear`;
    bar.style.width = "100%";

    autoplayTimerId = window.setTimeout(() => {
      autoplayTimerId = null;
      if (!mq.matches || motionMq.matches) return;
      const next = (getActiveIndex() + 1) % items.length;
      setActive(next);
      startAutoplayCycle();
    }, intervalMs);
  }

  function setActive(index) {
    const next = Math.min(Math.max(index, 0), items.length - 1);

    items.forEach((item, i) => {
      const on = i === next;
      item.classList.toggle("is-active", on);
      const trigger = triggers[i];
      const panel = item.querySelector(".studio-slider__panel");

      if (mq.matches) {
        trigger?.setAttribute("aria-expanded", on ? "true" : "false");
        trigger?.setAttribute("tabindex", on ? "0" : "-1");
        panel?.setAttribute("aria-hidden", on ? "false" : "true");
      } else {
        trigger?.setAttribute("aria-expanded", "true");
        trigger?.removeAttribute("tabindex");
        panel?.removeAttribute("aria-hidden");
      }
    });

    visuals.forEach((visual, i) => {
      visual.classList.toggle("is-active", i === next);
    });
  }

  function onTriggerClick(event) {
    if (!mq.matches) return;
    const item = event.currentTarget.closest("[data-studio-slider-item]");
    if (!item) return;
    const index = items.indexOf(item);
    if (index < 0) return;
    setActive(index);
    startAutoplayCycle();
  }

  function onKeydown(event) {
    if (!mq.matches) return;
    const focused = triggers.indexOf(document.activeElement);
    if (focused < 0) return;

    let next = focused;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      next = Math.min(focused + 1, items.length - 1);
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      next = Math.max(focused - 1, 0);
    } else if (event.key === "Home") {
      next = 0;
    } else if (event.key === "End") {
      next = items.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    setActive(next);
    startAutoplayCycle();
    triggers[next]?.focus();
  }

  function applyMode() {
    setActive(getActiveIndex());
    if (!mq.matches) {
      stopAutoplay();
      return;
    }
    if (motionMq.matches) {
      stopAutoplay();
      applyReducedMotionProgress();
      return;
    }
    startAutoplayCycle();
  }

  let resizeMeasureTimer = null;
  const PANEL_SLOT_BUFFER_PX = 8;

  /**
   * Desktop: set `--studio-slider-panel-slot` to the tallest open panel so the
   * accordion column keeps constant height (0fr/1fr grid was resizing the row).
   */
  function measureAndApplyPanelSlot() {
    if (!mq.matches) {
      root.style.removeProperty("--studio-slider-panel-slot");
      return;
    }

    const saved = getActiveIndex();
    stopAutoplay();

    root.classList.add("studio-slider--measuring");

    let maxShell = 0;
    for (let i = 0; i < items.length; i++) {
      setActive(i);
      void root.offsetHeight;
      const shell = items[i].querySelector(".studio-slider__panel-shell");
      if (shell) {
        maxShell = Math.max(maxShell, shell.offsetHeight);
      }
    }

    root.classList.remove("studio-slider--measuring");

    if (maxShell > 0) {
      root.style.setProperty(
        "--studio-slider-panel-slot",
        `${maxShell + PANEL_SLOT_BUFFER_PX}px`,
      );
    }
    setActive(saved);
  }

  function bumpPanelSlotIfNeeded() {
    if (!mq.matches || root.classList.contains("studio-slider--measuring")) {
      return;
    }

    const idx = getActiveIndex();
    const shell = items[idx]?.querySelector(".studio-slider__panel-shell");
    if (!shell) return;

    const needed = shell.scrollHeight;
    const cur = parseFloat(
      getComputedStyle(root).getPropertyValue("--studio-slider-panel-slot"),
    );
    const floor = Number.isFinite(cur) ? cur : 0;

    if (needed > floor + 1) {
      root.style.setProperty(
        "--studio-slider-panel-slot",
        `${needed + PANEL_SLOT_BUFFER_PX}px`,
      );
    }
  }

  let slotGrowRaf = 0;
  const slotResizeObserver = new ResizeObserver(() => {
    if (!mq.matches) return;
    cancelAnimationFrame(slotGrowRaf);
    slotGrowRaf = requestAnimationFrame(() => {
      slotGrowRaf = 0;
      bumpPanelSlotIfNeeded();
    });
  });
  slotResizeObserver.observe(root);

  function remeasureAndApplyMode() {
    measureAndApplyPanelSlot();
    applyMode();
  }

  function scheduleLayoutMeasure() {
    if (!mq.matches) return;
    window.clearTimeout(resizeMeasureTimer);
    resizeMeasureTimer = window.setTimeout(() => {
      resizeMeasureTimer = null;
      remeasureAndApplyMode();
    }, 150);
  }

  triggers.forEach((trigger) => {
    trigger.addEventListener("click", onTriggerClick);
  });
  root.addEventListener("keydown", onKeydown);
  mq.addEventListener("change", remeasureAndApplyMode);
  motionMq.addEventListener("change", applyMode);
  window.addEventListener("resize", scheduleLayoutMeasure);

  remeasureAndApplyMode();

  if (document.fonts?.ready) {
    document.fonts.ready.then(() => {
      if (!root.isConnected) return;
      remeasureAndApplyMode();
    });
  }
}

/** Subtle device image follow on pointer position (`.pricing__image`). */
function initPricingDeviceParallax() {
  const wrap = document.querySelector(".pricing__image[data-pricing-parallax]");
  if (!wrap) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  if (!window.matchMedia("(pointer: fine)").matches) return;

  const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

  wrap.addEventListener("pointermove", (e) => {
    if (e.pointerType !== "mouse") return;
    const r = wrap.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    const nx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1);
    const ny = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1);
    wrap.style.setProperty("--pricing-parallax-x", nx.toFixed(4));
    wrap.style.setProperty("--pricing-parallax-y", ny.toFixed(4));
  });

  wrap.addEventListener("pointerleave", () => {
    wrap.style.setProperty("--pricing-parallax-x", "0");
    wrap.style.setProperty("--pricing-parallax-y", "0");
  });
}

function initStudioSlider() {
  document.querySelectorAll("[data-studio-slider]").forEach((root) => {
    initStudioSliderRoot(root);
  });
  initPricingDeviceParallax();
}

/** Slick carousel for the mobile customize-slider row (≤1199px). */
function initCustomizeSliderSlick() {
  const $ = window.jQuery;
  if (!$?.fn?.slick) return;

  const mobileSlider = $(".js-customize-slider--mobile");
  if (!mobileSlider.length) return;

  let isMobileSliderInitialized = false;

  function initMobileSlider() {
    if (
      window.innerWidth <= CUSTOMIZE_SLICK_BREAKPOINT &&
      !isMobileSliderInitialized
    ) {
      if (!mobileSlider.hasClass("slick-initialized")) {
        mobileSlider.slick({
          slidesToShow: 1,
          slidesToScroll: 1,
          draggable: true,
          swipe: true,
          touchMove: true,
          infinite: true,
          speed: 300,
          cssEase: "ease",
          variableWidth: true,
          fade: false,
          useTransform: true,
          arrows: false,
          dots: false,
        });
        isMobileSliderInitialized = true;
      }
    } else if (
      window.innerWidth > CUSTOMIZE_SLICK_BREAKPOINT &&
      isMobileSliderInitialized
    ) {
      if (mobileSlider.hasClass("slick-initialized")) {
        mobileSlider.slick("unslick");
        isMobileSliderInitialized = false;
      }
    }
  }

  initMobileSlider();
  $(window).on("resize", initMobileSlider);
}

/**
 * Desktop: infinite horizontal rows, drag, scroll-linked motion (GSAP + ScrollTrigger).
 */
function initCustomizeSliderAnimation() {
  const motionMq = window.matchMedia(REDUCED_MOTION_MQ);
  if (motionMq.matches) return;

  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;

  gsap.registerPlugin(ScrollTrigger);

  const wrapper = document.querySelector(".customize-slider__wrapper");
  const firstRow = document.querySelector(".js-customize-slider--first");
  const secondRow = document.querySelector(".js-customize-slider--second");

  if (!wrapper || !firstRow || !secondRow) return;

  const duplicateSlides = (row) => {
    const slides = Array.from(row.children);
    slides.forEach((slide) => {
      row.appendChild(slide.cloneNode(true));
    });
  };

  duplicateSlides(firstRow);
  duplicateSlides(secondRow);

  const rows = [firstRow, secondRow];

  let currentPosition = 0;
  let targetPosition = 0;
  let isDragging = false;
  let scrollBasePosition = 0;

  const lerp = (a, b, n) => (1 - n) * a + n * b;
  const lerpEase = 0.1;

  let startX;
  let startY;
  let startPos;
  let isDraggingHorizontally = false;

  const customCursor = document.querySelector(".custom-cursor");

  const updateCursorScale = () => {
    if (customCursor) {
      const scale = isDragging ? 0.7 : 1;
      customCursor.style.setProperty("--cursor-scale", String(scale));
    }
  };

  const start = (e) => {
    startX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;
    startY = e.type.includes("touch") ? e.touches[0].clientY : e.clientY;
    startPos = targetPosition;
    isDraggingHorizontally = false;
    isDragging = false;
    if (!e.type.includes("touch")) e.preventDefault();
  };

  const move = (e) => {
    const currentX = e.type.includes("touch")
      ? e.touches[0].clientX
      : e.clientX;
    const currentY = e.type.includes("touch")
      ? e.touches[0].clientY
      : e.clientY;
    const deltaX = Math.abs(currentX - startX);
    const deltaY = Math.abs(currentY - startY);

    if (!isDraggingHorizontally && !isDragging) {
      if (deltaX > 5 || deltaY > 5) {
        if (deltaX > deltaY * 1.5) {
          isDraggingHorizontally = true;
          isDragging = true;
          if (customCursor) {
            customCursor.classList.add("is-dragging");
          }
          updateCursorScale();
        } else return;
      } else return;
    }

    if (!isDragging) return;

    if (e.type.includes("touch")) e.preventDefault();
    const newPos = startPos + currentX - startX;
    targetPosition = newPos;
  };

  const end = (e) => {
    isDragging = false;
    scrollBasePosition = targetPosition;
    if (customCursor) {
      customCursor.classList.remove("is-dragging");
    }
    updateCursorScale();
    const isTouchDevice = e && e.type && e.type.includes("touch");
    if (isTouchDevice) isDraggingHorizontally = false;
  };

  wrapper.addEventListener("mousedown", start);
  wrapper.addEventListener("touchstart", start, { passive: true });
  wrapper.addEventListener("mousemove", move);
  wrapper.addEventListener("touchmove", move, { passive: false });
  wrapper.addEventListener("mouseup", end);
  wrapper.addEventListener("touchend", end);

  ScrollTrigger.create({
    trigger: wrapper,
    start: "top 90%",
    end: "bottom 10%",
    scrub: 2,
    onUpdate: (self) => {
      if (!isDragging) {
        const scrollProgress = self.progress;
        const scrollRange = firstRow.scrollWidth * 0.2;
        targetPosition = scrollBasePosition - scrollProgress * scrollRange;
      }
    },
  });

  const getLoopWidth = (row) => row.scrollWidth / 2;
  const wrap = (value, min, max) => {
    const range = max - min;
    return ((((value - min) % range) + range) % range) + min;
  };

  const update = () => {
    const lerpValue = isDragging ? 0.25 : lerpEase;
    currentPosition += (targetPosition - currentPosition) * lerpValue;

    rows.forEach((row) => {
      const loopWidth = getLoopWidth(firstRow);
      const wrappedX = wrap(currentPosition, -loopWidth, 0);

      gsap.set(row, {
        x: wrappedX,
        y: 0,
        force3D: true,
      });
    });

    requestAnimationFrame(update);
  };

  update();
}

/** “DRAG” cursor over the customize slider (desktop only). */
function initCustomizeCursor() {
  const slider = document.querySelector(".customize-slider__wrapper");
  if (!slider) return;

  const cursor = document.createElement("div");
  cursor.className = "custom-cursor";

  const text = document.createElement("span");
  text.textContent = "DRAG";

  cursor.appendChild(text);
  document.body.appendChild(cursor);

  let mouseX = 0;
  let mouseY = 0;
  let cursorX = 0;
  let cursorY = 0;

  const lerp = (a, b, n) => (1 - n) * a + n * b;

  function animate() {
    cursorX = lerp(cursorX, mouseX, 0.15);
    cursorY = lerp(cursorY, mouseY, 0.15);

    cursor.style.transform = `translate(${cursorX}px, ${cursorY}px) translate(-50%, -50%) scale(var(--cursor-scale, 1))`;
    requestAnimationFrame(animate);
  }
  animate();

  window.addEventListener("mousemove", (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  });

  slider.addEventListener("mouseenter", () => {
    if (window.innerWidth >= 1200) {
      cursor.classList.add("is-active");
    }
  });

  slider.addEventListener("mouseleave", () => {
    cursor.classList.remove("is-active");
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth < 1200) {
      cursor.classList.remove("is-active");
    }
  });
}

function initCustomizeSliderOnLoad() {
  if (!document.querySelector(".customize-slider__wrapper")) return;

  initCustomizeCursor();
  initCustomizeSliderSlick();
  initCustomizeSliderAnimation();
}

/**
 * Accordion for `.wrg.boost-performance` (ported from legacy StudioSwift `script.js`).
 * Keeps icon `src` swapping on `.icon-arrow-down img` — do not run imgToSvg on those nodes.
 */
function initBoostPerformanceAccordion() {
  const $ = window.jQuery;
  if (!$?.fn) return;

  const $boost = $(".wrg.boost-performance");
  if (!$boost.length) return;

  const closeIconSrc = "https://cdn.whiterabbit.group/images/icons/minus-white.svg";
  const openIconSrc = "https://cdn.whiterabbit.group/images/icons/accordion-open.svg";

  $boost.find(".accordion button.trigger").on("click", function (j) {
    j.preventDefault();
    const $btn = $(this)
    const $parent = $btn.parents("li").eq(0);
    const cls = "active";
    const $content = $parent.find(".accordion-inner");
    const $icons = $btn.find(".icon-arrow-down img");

    if ($parent.hasClass(cls)) {
      $icons.attr("src", openIconSrc);
      $content.slideUp(320);
      window.setTimeout(() => {
        $parent.removeClass(cls);
      }, 320);
      return;
    }

    const $accordion = $boost.find(".accordion");
    const $activeItems = $accordion.find("li.accordion-item.active");
    if ($activeItems.length) {
      $activeItems.each(function () {
        $(this).find(".icon-arrow-down img").attr("src", openIconSrc);
        const $item = $(this);
        $item.find(".accordion-inner").slideUp(500);
        window.setTimeout(() => {
          $item.removeClass(cls);
        }, 500);
      });
    }

    $parent.addClass(cls);
    $icons.attr("src", closeIconSrc);
    $content.slideDown(300);
  });
}

/** Inline SVG for `img.svg` (title cards, etc.). Skips chevrons inside boost accordion so `src` swaps still work. */
function initStudioSwiftImgToSvg() {
  const images = document.querySelectorAll("img.svg");
  images.forEach((image) => {
    if (image.closest(".wrg.boost-performance .icon-arrow-down")) return;

    fetch(image.src)
      .then((res) => res.text())
      .then((data) => {
        const parser = new DOMParser();
        const svg = parser
          .parseFromString(data, "image/svg+xml")
          .querySelector("svg");
        if (!svg) return;

        const w = svg.getAttribute("width");
        const h = svg.getAttribute("height");
        if (w && h) {
          svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
        }
        if (image.id) svg.id = image.id;
        if (image.className) svg.classList = image.classList;
        image.parentNode?.replaceChild(svg, image);
      })
      .catch(() => {});
  });
}

function initStudioSwiftOnLoad() {
  initBoostPerformanceAccordion();
  initStudioSwiftImgToSvg();
  initCustomizeSliderOnLoad();
}

document.addEventListener("DOMContentLoaded", initStudioSlider);
window.addEventListener("load", initStudioSwiftOnLoad);
